import { useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { useGetIdentity } from "ra-core";
import {
  Play,
  Copy,
  Check,
  ChevronDown,
  ChevronUp,
  Code2,
  Globe,
  Zap,
  BookOpen,
  Filter,
  ShieldCheck,
  RefreshCw,
  Workflow,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { getSupabaseClient } from "../providers/supabase/supabase";
import { syncPipelinesToSupabase } from "../deals/usePipelines";

const API_BASE_URL = `${window.location.origin}/api`;
const KEY_PLACEHOLDER = "<sua-api-key>";

// ─── Types ────────────────────────────────────────────────────────────────────

type HttpMethod = "GET" | "POST" | "PATCH" | "DELETE";

interface QueryParam {
  name: string;
  description: string;
}

interface Endpoint {
  label: string;
  path: string;
  methods: HttpMethod[];
  description: string;
  params: QueryParam[];
  sampleQuery?: string;
  sampleBody?: object;
  samplePatchBody?: object;
}

// ─── Endpoints catalogue ─────────────────────────────────────────────────────

const ENDPOINTS: Endpoint[] = [
  {
    label: "Leads (Negócios)",
    path: "leads",
    methods: ["GET", "POST", "PATCH", "DELETE"],
    description:
      "Gerencia negócios. No POST, suporta Smart Payload (cria/vincula empresa e contato automaticamente). O GET retorna tasks e histórico de anotações.",
    params: [
      { name: "id", description: "Buscar / atualizar / deletar por ID" },
      { name: "funil_id", description: "Filtrar por funil (ID ou nome)" },
      { name: "status", description: "aberto / ganho / perdido" },
      { name: "search", description: "Busca por nome" },
      { name: "limit", description: "Máx. de registros (padrão 50)" },
    ],
    sampleQuery: "limit=10",
    sampleBody: {
      name: "Contrato BPO Financeiro 2026",
      valor: 75000,
      funil_id: 375,
      stage: "lead",
      empresa: {
        name: "Tech Soluções Empresariais Ltda",
        cnpj: "12.345.678/0001-90",
      },
      contato: {
        name: "Juliana Menezes",
        email: "juliana.menezes@techsolucoes.com.br",
        phone: "(11) 97777-6666",
      },
      descricao: "Lead qualificado vindo de automação",
      custom_fields: { origem: "n8n" },
    },
    samplePatchBody: {
      valor: 85000,
      stage: "opportunity",
      status: "aberto",
      descricao: "Valor renegociado em reunião",
    },
  },
  {
    label: "Contatos (Pessoas)",
    path: "contacts",
    methods: ["GET", "POST", "PATCH", "DELETE"],
    description:
      "Gerencia pessoas e contatos do CRM. Aceita 'name' completo ou first_name/last_name e vincula a empresa por ID ou nome.",
    params: [
      { name: "id", description: "Buscar / atualizar / deletar por ID" },
      { name: "company_id", description: "Filtrar por empresa" },
      { name: "search", description: "Busca por nome" },
      { name: "limit", description: "Máx. de registros (padrão 50)" },
    ],
    sampleQuery: "limit=10",
    sampleBody: {
      name: "Juliana Menezes",
      email: "juliana.menezes@techsolucoes.com.br",
      phone: "(11) 97777-6666",
      title: "Gerente de Compras",
      company_name: "Tech Soluções Empresariais Ltda",
      custom_fields: { canal_preferido: "whatsapp" },
    },
    samplePatchBody: {
      title: "Diretora Comercial",
      phone: "(11) 99999-0000",
    },
  },
  {
    label: "Empresas",
    path: "companies",
    methods: ["GET", "POST", "PATCH", "DELETE"],
    description:
      "Gerencia empresas parceiras ou clientes. Suporta busca por CNPJ, cidade, setor e flag 'upsert: true'.",
    params: [
      { name: "id", description: "Buscar / atualizar / deletar por ID" },
      { name: "cnpj", description: "Filtrar por CNPJ" },
      { name: "search", description: "Busca por nome" },
      { name: "limit", description: "Máx. de registros (padrão 50)" },
    ],
    sampleQuery: "limit=10",
    sampleBody: {
      nome: "Tech Soluções Empresariais Ltda",
      cnpj: "12.345.678/0001-90",
      telefone: "(11) 98888-7777",
      website: "https://techsolucoes.com.br",
      setor: "Tecnologia",
      cidade: "São Paulo",
      uf: "SP",
      upsert: true,
    },
    samplePatchBody: {
      telefone: "(11) 97777-8888",
      cidade: "Campinas",
    },
  },
  {
    label: "Atividades do Negócio (tasks)",
    path: "tasks",
    methods: ["GET", "POST", "PATCH", "DELETE"],
    description:
      "Atividades e tarefas do CRM vinculadas ao negócio (deal_id), empresa (company_id) ou contato (contact_id).",
    params: [
      { name: "id", description: "ID da atividade" },
      { name: "deal_id", description: "Atividades de um negócio" },
      { name: "contact_id", description: "Atividades de um contato" },
      { name: "limit", description: "Máx. de registros" },
    ],
    sampleQuery: "deal_id=1",
    sampleBody: {
      deal_id: 1,
      type: "Call",
      text: "Reunião de alinhamento com cliente",
      prioridade: "alta",
      due_date: "2026-10-06T14:00:00Z",
      notes: "Apresentar nova proposta comercial",
    },
    samplePatchBody: {
      prioridade: "baixa",
      notes: "Reunião remarcada para próxima semana",
    },
  },
  {
    label: "Anotações do Negócio (deal_notes)",
    path: "deal_notes",
    methods: ["GET", "POST", "PATCH", "DELETE"],
    description:
      "Notas, observações e histórico de interações vinculadas a um negócio (deal_id).",
    params: [
      { name: "id", description: "ID da anotação" },
      { name: "deal_id", description: "Filtrar por negócio" },
      { name: "limit", description: "Máx. de registros" },
    ],
    sampleQuery: "deal_id=1",
    sampleBody: {
      deal_id: 1,
      type: "Reunião",
      text: "Cliente solicitou adequação nos valores do escopo.",
    },
    samplePatchBody: {
      text: "Cliente aceitou nova minuta de proposta.",
    },
  },
  {
    label: "Anotações do Contato (contact_notes)",
    path: "contact_notes",
    methods: ["GET", "POST", "PATCH", "DELETE"],
    description: "Notas e anotações vinculadas ao contato (contact_id).",
    params: [
      { name: "id", description: "ID da anotação" },
      { name: "contact_id", description: "Filtrar por contato" },
      { name: "limit", description: "Máx. de registros" },
    ],
    sampleQuery: "contact_id=1",
    sampleBody: {
      contact_id: 1,
      text: "Contato prefere ser chamado pelo WhatsApp pela manhã.",
    },
    samplePatchBody: {
      text: "Contato atualizado com sucesso.",
    },
  },
  {
    label: "Funis (Pipelines)",
    path: "pipelines",
    methods: ["GET", "POST", "PATCH", "DELETE"],
    description:
      "Consulta, cria e edita funis de vendas. O GET retorna todas as etapas com seus IDs numéricos e contadores de leads.",
    params: [{ name: "id", description: "Buscar ou alterar funil por ID" }],
    sampleQuery: "",
    sampleBody: {
      name: "Funil Comercial Enterprise",
      description: "Pipeline focado em grandes clientes",
      is_default: false,
      order_nr: 2,
    },
    samplePatchBody: {
      description: "Pipeline comercial revisado",
    },
  },
  {
    label: "Etapas do Funil (Stages)",
    path: "pipeline_stages",
    methods: ["GET", "POST", "PATCH", "DELETE"],
    description:
      "Gerencia etapas individuais de cada pipeline. Cada etapa possui ID numérico, slug, cor e totais de negócios.",
    params: [
      { name: "id", description: "ID da etapa" },
      { name: "pipeline_id", description: "Filtrar por funil" },
    ],
    sampleQuery: "pipeline_id=375",
    sampleBody: {
      pipeline_id: 375,
      name: "Apresentação da Proposta",
      slug: "apresentacao-proposta",
      order_nr: 3,
      color: "#6366f1",
    },
    samplePatchBody: {
      name: "Proposta Enviada",
      color: "#10b981",
    },
  },
  {
    label: "Tags / Etiquetas",
    path: "tags",
    methods: ["GET", "POST", "PATCH", "DELETE"],
    description:
      "Etiquetas e marcadores utilizados para categorizar contatos e empresas.",
    params: [
      { name: "id", description: "ID da tag" },
      { name: "search", description: "Buscar por nome" },
    ],
    sampleQuery: "",
    sampleBody: {
      name: "Cliente VIP",
      color: "#e11d48",
    },
    samplePatchBody: {
      color: "#059669",
    },
  },
  {
    label: "Usuários / Vendedores (sales)",
    path: "sales",
    methods: ["GET"],
    description:
      "Consulta usuários, operadores e vendedores cadastrados no CRM.",
    params: [
      { name: "id", description: "ID do vendedor" },
      { name: "search", description: "Busca por nome ou e-mail" },
    ],
    sampleQuery: "",
  },
  {
    label: "Definições de Custom Fields",
    path: "custom_field_definitions",
    methods: ["GET", "POST", "PATCH", "DELETE"],
    description:
      "Identificadores (keys), tipos e configurações dos campos customizados de leads, contatos, empresas e tarefas.",
    params: [
      { name: "id", description: "ID do campo customizado" },
      { name: "entity_type", description: "deal / contact / company / task" },
    ],
    sampleQuery: "entity_type=deal",
    sampleBody: {
      entity_type: "deal",
      key: "ramo_atuacao",
      label: "Ramo de Atuação",
      field_type: "select",
      options: ["Tecnologia", "Varejo", "Serviços", "Indústria"],
      required: false,
      order_nr: 1,
    },
    samplePatchBody: {
      label: "Segmento / Ramo de Atuação",
    },
  },
];

// ─── Helpers ─────────────────────────────────────────────────────────────────

function methodColor(m: HttpMethod) {
  return {
    GET: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30",
    POST: "bg-blue-500/15 text-blue-400 border-blue-500/30",
    PATCH: "bg-amber-500/15 text-amber-400 border-amber-500/30",
    DELETE: "bg-red-500/15 text-red-400 border-red-500/30",
  }[m];
}

function buildUrl(path: string, query: string, method?: HttpMethod) {
  const base = `${API_BASE_URL}/${path}`;
  const q = query.trim().replace(/^\?/, "");
  if (q && method !== "POST") {
    return `${base}?${q}`;
  }
  return base;
}

function buildCurl(
  url: string,
  method: HttpMethod,
  body: string,
  apiKey: string,
) {
  let cmd = `curl -X ${method} '${url}' \\\n  -H 'x-api-key: ${apiKey || KEY_PLACEHOLDER}'`;
  if (["POST", "PATCH"].includes(method) && body.trim()) {
    cmd += ` \\\n  -H 'Content-Type: application/json' \\\n  -d '${body.trim()}'`;
  }
  return cmd;
}

function buildFetch(
  url: string,
  method: HttpMethod,
  body: string,
  apiKey: string,
) {
  const hasBody = ["POST", "PATCH"].includes(method) && body.trim();
  return `fetch('${url}', {
  method: '${method}',
  headers: {
    'x-api-key': '${apiKey || KEY_PLACEHOLDER}',${hasBody ? "\n    'Content-Type': 'application/json'," : ""}
  },${hasBody ? `\n  body: JSON.stringify(${body.trim()}),` : ""}
})
  .then(r => r.json())
  .then(console.log);`;
}

function buildN8n(url: string, method: HttpMethod, apiKey: string) {
  return `Nó: HTTP Request
Method: ${method}
URL: ${url}
Authentication: None
Send Headers: ON
  Name:  x-api-key
  Value: ${apiKey || KEY_PLACEHOLDER}${
    ["POST", "PATCH"].includes(method)
      ? `
Send Body: ON
  Body Content Type: JSON
  Specify Body: Using JSON
  JSON: (cole o Request Body acima)`
      : ""
  }`;
}

// ─── CopyButton ───────────────────────────────────────────────────────────────

const CopyButton = ({ text }: { text: string }) => {
  const [copied, setCopied] = useState(false);
  const handle = () => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };
  return (
    <Button
      variant="ghost"
      size="sm"
      onClick={handle}
      className="h-7 w-7 p-0"
      title="Copiar"
    >
      {copied ? (
        <Check className="h-3.5 w-3.5 text-emerald-400" />
      ) : (
        <Copy className="h-3.5 w-3.5" />
      )}
    </Button>
  );
};

// ─── JsonViewer ───────────────────────────────────────────────────────────────

const JsonViewer = ({
  data,
  status,
}: {
  data: unknown;
  status: number | null;
}) => {
  const isError = status !== null && (status >= 400 || status === 0);
  const text = JSON.stringify(data, null, 2);

  return (
    <div className="relative">
      <div className="absolute top-2 right-2 z-10 flex items-center gap-1">
        {status !== null && (
          <Badge
            variant="outline"
            className={
              isError
                ? "text-red-400 border-red-500/40 bg-red-500/10"
                : "text-emerald-400 border-emerald-500/40 bg-emerald-500/10"
            }
          >
            {status}
          </Badge>
        )}
        <CopyButton text={text} />
      </div>
      <pre
        className={`rounded-lg p-4 text-xs font-mono overflow-auto max-h-96 leading-relaxed ${
          isError ? "bg-red-950/40 text-red-300" : "bg-zinc-900 text-zinc-200"
        }`}
      >
        {text}
      </pre>
    </div>
  );
};

// ─── CodeSnippet ──────────────────────────────────────────────────────────────

const CodeSnippet = ({
  label,
  code,
  icon,
}: {
  label: string;
  code: string;
  icon: React.ReactNode;
}) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-lg border border-zinc-800 bg-zinc-900/50 overflow-hidden">
      <button
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center justify-between px-4 py-2.5 text-sm font-medium text-zinc-300 hover:bg-zinc-800/60 transition-colors"
      >
        <span className="flex items-center gap-2">
          {icon}
          {label}
        </span>
        {open ? (
          <ChevronUp className="h-4 w-4" />
        ) : (
          <ChevronDown className="h-4 w-4" />
        )}
      </button>
      {open && (
        <div className="relative border-t border-zinc-800">
          <div className="absolute top-2 right-2">
            <CopyButton text={code} />
          </div>
          <pre className="p-4 text-xs font-mono text-zinc-300 overflow-auto whitespace-pre-wrap">
            {code}
          </pre>
        </div>
      )}
    </div>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────

export const ApiPlayground = () => {
  const { identity } = useGetIdentity();

  const [selected, setSelected] = useState<Endpoint>(ENDPOINTS[0]);
  const [method, setMethod] = useState<HttpMethod>("GET");
  const [query, setQuery] = useState(ENDPOINTS[0].sampleQuery ?? "");
  const [body, setBody] = useState(
    JSON.stringify(ENDPOINTS[0].sampleBody ?? {}, null, 2),
  );
  const [apiKey, setApiKey] = useState("");
  const [loading, setLoading] = useState(false);
  const [response, setResponse] = useState<unknown>(null);
  const [status, setStatus] = useState<number | null>(null);
  const [usedApiKey, setUsedApiKey] = useState(false);
  const [syncing, setSyncing] = useState(false);

  const url = buildUrl(selected.path, query, method);

  const resetResponse = () => {
    setResponse(null);
    setStatus(null);
    setUsedApiKey(false);
  };

  const handleEndpointChange = (label: string) => {
    const ep = ENDPOINTS.find((e) => e.label === label)!;
    setSelected(ep);
    const initialMethod = ep.methods[0];
    setMethod(initialMethod);
    setQuery(ep.sampleQuery ?? "");
    setBody(JSON.stringify(ep.sampleBody ?? {}, null, 2));
    resetResponse();
  };

  const handleMethodChange = (m: string) => {
    const newMethod = m as HttpMethod;
    setMethod(newMethod);
    if (newMethod === "PATCH" && selected.samplePatchBody) {
      setBody(JSON.stringify(selected.samplePatchBody, null, 2));
    } else if (newMethod === "POST" && selected.sampleBody) {
      setBody(JSON.stringify(selected.sampleBody, null, 2));
    }
    resetResponse();
  };

  const run = useCallback(async () => {
    setLoading(true);
    resetResponse();

    try {
      let token = apiKey.trim();
      if (!token) {
        const {
          data: { session },
        } = await getSupabaseClient().auth.getSession();
        token = session?.access_token ?? "";
      }

      if (!token) {
        setStatus(401);
        setResponse({
          code: "AUTH_REQUIRED",
          message:
            "Informe uma API Key ou faça login novamente no CRM para testar.",
        });
        return;
      }

      let parsedBody: string | undefined;
      if (["POST", "PATCH"].includes(method) && body.trim()) {
        try {
          JSON.parse(body);
          parsedBody = body;
        } catch {
          setStatus(400);
          setResponse({ error: "JSON inválido no body" });
          return;
        }
      }

      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 15000);

      try {
        const res = await fetch(url, {
          method,
          headers: {
            "x-api-key": token,
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          body: parsedBody,
          signal: controller.signal,
        });
        setStatus(res.status);
        const text = await res.text();
        try {
          setResponse(JSON.parse(text));
        } catch {
          setResponse({ raw: text });
        }
        if (res.ok && apiKey.trim()) setUsedApiKey(true);
      } catch (fetchErr: any) {
        if (fetchErr.name === "AbortError") {
          setStatus(504);
          setResponse({
            code: "TIMEOUT",
            message: "A API demorou mais de 15s para responder.",
          });
          return;
        }
        throw fetchErr;
      } finally {
        clearTimeout(timeoutId);
      }
    } catch (err: any) {
      setStatus(0);
      setResponse({ error: err?.message ?? "Erro de rede" });
    } finally {
      setLoading(false);
    }
  }, [url, method, body, apiKey]);

  const curlCode = buildCurl(url, method, body, apiKey.trim());
  const fetchCode = buildFetch(url, method, body, apiKey.trim());
  const n8nCode = buildN8n(url, method, apiKey.trim());

  const handleSyncFromCRM = async () => {
    setSyncing(true);
    try {
      await syncPipelinesToSupabase();
      await run();
    } catch (err) {
      console.error("Erro ao sincronizar pipelines:", err);
    } finally {
      setSyncing(false);
    }
  };

  return (
    <div className="space-y-6 pb-10">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-violet-500/15 border border-violet-500/30">
            <Zap className="h-5 w-5 text-violet-400" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-foreground">
              API Playground
            </h1>
            <p className="text-sm text-muted-foreground">
              Teste todos os endpoints da API nativa do Dotus CRM em tempo real
            </p>
          </div>
        </div>

        <Button
          variant="outline"
          size="sm"
          asChild
          className="gap-2 border-violet-500/40 text-violet-300 hover:text-violet-200 hover:bg-violet-500/10"
        >
          <Link to="/api-docs">
            <BookOpen className="h-4 w-4 text-violet-400" />
            Guia & Documentação da API
          </Link>
        </Button>
      </div>

      {/* Base URL info */}
      <Card className="border-zinc-800 bg-zinc-950/60">
        <CardContent className="pt-4 pb-3">
          <div className="flex items-center gap-2 text-xs text-zinc-400">
            <Globe className="h-3.5 w-3.5 text-zinc-500 shrink-0" />
            <span className="font-mono truncate">{API_BASE_URL}/</span>
            <CopyButton text={`${API_BASE_URL}/`} />
            <Badge
              variant="outline"
              className="ml-auto text-[10px] text-violet-300 border-violet-500/40"
            >
              Header: x-api-key
            </Badge>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* ── Left: Builder ───────────────────────────────────── */}
        <div className="lg:col-span-2 space-y-4">
          {/* Endpoint + method */}
          <Card className="border-zinc-800 bg-zinc-950/60">
            <CardHeader className="pb-3 flex flex-row items-center justify-between">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <BookOpen className="h-4 w-4 text-violet-400" />
                Recurso e Método
              </CardTitle>
              {selected.path === "pipelines" && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleSyncFromCRM}
                  disabled={syncing}
                  className="h-7 text-xs border-violet-500/40 text-violet-300 hover:bg-violet-500/10 gap-1.5"
                >
                  <RefreshCw
                    className={`h-3 w-3 ${syncing ? "animate-spin" : ""}`}
                  />
                  {syncing
                    ? "Sincronizando..."
                    : "Sincronizar Funis do CRM com o Banco"}
                </Button>
              )}
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="grid grid-cols-3 gap-3">
                <div className="col-span-2">
                  <Label className="text-xs mb-1.5 block">Recurso</Label>
                  <Select
                    value={selected.label}
                    onValueChange={handleEndpointChange}
                  >
                    <SelectTrigger className="bg-zinc-900 border-zinc-700 text-zinc-200">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {ENDPOINTS.map((ep) => (
                        <SelectItem key={ep.label} value={ep.label}>
                          <span className="flex items-center gap-2">
                            {ep.label}
                            <span className="font-mono text-[10px] text-zinc-500">
                              /api/{ep.path}
                            </span>
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label className="text-xs mb-1.5 block">Método</Label>
                  <Select value={method} onValueChange={handleMethodChange}>
                    <SelectTrigger
                      className={`border font-mono font-bold ${methodColor(method)} bg-transparent`}
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {selected.methods.map((m) => (
                        <SelectItem key={m} value={m}>
                          <span
                            className={`font-mono font-bold ${methodColor(m)} px-1.5 py-0.5 rounded text-xs`}
                          >
                            {m}
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Live URL */}
              <div className="rounded-lg bg-zinc-900 border border-zinc-800 px-3 py-2 flex items-center gap-2">
                <span
                  className={`font-mono font-bold text-xs px-1.5 py-0.5 rounded ${methodColor(method)}`}
                >
                  {method}
                </span>
                <span className="font-mono text-xs text-zinc-300 truncate flex-1">
                  {url}
                </span>
                <CopyButton text={url} />
              </div>

              <p className="text-xs text-muted-foreground">
                {selected.description}
              </p>
            </CardContent>
          </Card>

          {/* Query params */}
          {(method === "GET" || method === "DELETE" || method === "PATCH") && (
            <Card className="border-zinc-800 bg-zinc-950/60">
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium flex items-center gap-2">
                  <Filter className="h-4 w-4 text-amber-400" />
                  Query / Filtros
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div>
                  <Label className="text-xs mb-1.5 block text-muted-foreground">
                    Parâmetros (formato chave=valor&amp;chave2=valor2)
                  </Label>
                  <Input
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder="id=1&limit=10"
                    className="font-mono text-xs bg-zinc-900 border-zinc-700 text-zinc-200"
                  />
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {selected.params.map((p) => (
                    <button
                      key={p.name}
                      title={p.description}
                      onClick={() =>
                        setQuery((q) =>
                          q.includes(`${p.name}=`)
                            ? q
                            : `${q}${q.trim() ? "&" : ""}${p.name}=`,
                        )
                      }
                      className="text-[11px] font-mono px-2 py-0.5 rounded border border-amber-500/30 bg-amber-950/30 text-amber-300 hover:bg-amber-900/40 transition-colors"
                    >
                      {p.name}
                    </button>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Body */}
          {(method === "POST" || method === "PATCH") && (
            <Card className="border-zinc-800 bg-zinc-950/60">
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium flex items-center gap-2">
                  <Code2 className="h-4 w-4 text-blue-400" />
                  Request Body (JSON)
                </CardTitle>
              </CardHeader>
              <CardContent>
                <Textarea
                  value={body}
                  onChange={(e) => setBody(e.target.value)}
                  rows={12}
                  className="font-mono text-xs bg-zinc-900 border-zinc-700 text-zinc-200 resize-y"
                  placeholder="{}"
                  spellCheck={false}
                />
              </CardContent>
            </Card>
          )}

          {/* Run button */}
          <Button
            onClick={run}
            disabled={loading}
            className="w-full bg-violet-600 hover:bg-violet-500 text-white"
            size="lg"
          >
            <Play className="h-4 w-4 mr-2" />
            {loading ? "Executando..." : "Executar Requisição"}
          </Button>

          {/* Response */}
          {response !== null && (
            <Card className="border-zinc-800 bg-zinc-950/60">
              <CardHeader className="pb-3 flex flex-row items-center justify-between">
                <CardTitle className="text-sm font-medium text-zinc-300">
                  Resposta
                </CardTitle>
                {usedApiKey && (
                  <Badge
                    variant="outline"
                    className="text-emerald-400 border-emerald-500/40 bg-emerald-500/10 text-xs flex items-center gap-1.5"
                  >
                    <ShieldCheck className="h-3.5 w-3.5" />
                    Autenticado com API Key
                  </Badge>
                )}
              </CardHeader>
              <CardContent>
                <JsonViewer data={response} status={status} />
              </CardContent>
            </Card>
          )}

          {/* Code snippets */}
          <div className="space-y-2">
            <p className="text-xs font-medium text-zinc-400 uppercase tracking-wider">
              Snippets de código
            </p>
            <CodeSnippet
              label="cURL"
              code={curlCode}
              icon={<Code2 className="h-3.5 w-3.5 text-amber-400" />}
            />
            <CodeSnippet
              label="JavaScript (fetch)"
              code={fetchCode}
              icon={<Code2 className="h-3.5 w-3.5 text-blue-400" />}
            />
            <CodeSnippet
              label="n8n (HTTP Request)"
              code={n8nCode}
              icon={<Workflow className="h-3.5 w-3.5 text-rose-400" />}
            />
          </div>
        </div>

        {/* ── Right: Auth + Reference ─────────────────────────── */}
        <div className="space-y-4">
          {/* API Key / Auth */}
          <Card className="border-zinc-800 bg-zinc-950/60">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium">
                Autenticação
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div>
                <Label className="text-xs mb-1.5 block text-muted-foreground">
                  API Key (opcional — usa sua sessão se vazio)
                </Label>
                <Input
                  value={apiKey}
                  onChange={(e) => {
                    setApiKey(e.target.value);
                    setUsedApiKey(false);
                  }}
                  placeholder="crm_xxxxxxxx... ou dts_..."
                  className="font-mono text-xs bg-zinc-900 border-zinc-700 text-zinc-200"
                  type="password"
                />
                <p className="text-[11px] text-muted-foreground mt-1">
                  Gere sua chave na aba{" "}
                  <a
                    href="#/profile"
                    className="text-violet-400 hover:underline"
                  >
                    Perfil
                  </a>
                  . Ela é preenchida automaticamente nos snippets.
                </p>
              </div>

              <div className="rounded-lg bg-zinc-900 border border-zinc-800 p-2.5 text-[11px] font-mono text-zinc-300 space-y-0.5">
                <p className="text-violet-300 font-sans text-xs mb-1">
                  Headers aceitos:
                </p>
                <p>x-api-key: &lt;chave&gt;</p>
                <p>Authorization: Bearer &lt;chave&gt;</p>
              </div>

              {identity && (
                <div className="rounded-lg bg-zinc-900 border border-zinc-800 p-2.5 text-xs space-y-0.5">
                  <p className="text-zinc-400">Usuário atual:</p>
                  <p className="text-zinc-200 font-medium">
                    {identity.fullName ?? identity.email}
                  </p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Quick reference */}
          <Card className="border-zinc-800 bg-zinc-950/60">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium">
                Parâmetros de /api/{selected.path}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-1.5 text-xs text-zinc-400">
              {selected.params.map((p) => (
                <div key={p.name} className="flex justify-between gap-2">
                  <code className="text-amber-400 bg-amber-950/30 px-1 rounded text-[10px]">
                    {p.name}
                  </code>
                  <span className="text-right">{p.description}</span>
                </div>
              ))}
            </CardContent>
          </Card>

          {/* Endpoints list */}
          <Card className="border-zinc-800 bg-zinc-950/60">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium">
                Todos os Endpoints ({ENDPOINTS.length})
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-1 max-h-[380px] overflow-y-auto pr-1">
              {ENDPOINTS.map((ep) => (
                <button
                  key={ep.label}
                  onClick={() => handleEndpointChange(ep.label)}
                  className={`w-full flex items-center justify-between px-2.5 py-1.5 rounded-lg text-xs transition-colors ${
                    selected.label === ep.label
                      ? "bg-violet-600/20 text-violet-300 border border-violet-500/30"
                      : "text-zinc-400 hover:bg-zinc-800 border border-transparent"
                  }`}
                >
                  <span className="font-medium truncate text-left mr-1">
                    {ep.label}
                  </span>
                  <span className="font-mono text-[10px] text-zinc-500 shrink-0">
                    /api/{ep.path}
                  </span>
                </button>
              ))}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

ApiPlayground.path = "/api-playground";
